/**
 * Catalogos administrables -- primera vez que estas 7 listas (bases
 * juridicas, categorias de datos/titulares, destinatarios, medidas de
 * seguridad, garantias de transferencia, criterios de conservacion) tienen
 * una interfaz real de edicion, tanto en este backend como en el Supabase
 * original (ver packages/shared/src/catalogs.ts). Solo institucional
 * escribe -- src/catalog-admin.ts en el backend.
 *
 * `code` es inmutable despues de creado (identidad estable que ya usan las
 * actividades existentes); "quitar" un item lo desactiva, nunca lo borra --
 * una actividad historica sigue mostrando el label de un codigo
 * desactivado, solo deja de ofrecerse en el asistente para actividades
 * nuevas.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createCatalogItem,
  fetchAdminCatalogs,
  updateCatalogItem,
  type AdminCatalogItem,
  type CatalogName,
} from '../lib/queries';
import { PageHeader, QueryState } from '../components/ui';

const CATALOG_LABEL: Record<CatalogName, string> = {
  legal_basis: 'Bases de licitud',
  data_category: 'Categorías de datos',
  subject_category: 'Categorías de titulares',
  recipient_type: 'Tipos de destinatario',
  security_measure: 'Medidas de seguridad',
  transfer_guarantee_type: 'Garantías de transferencia',
  retention_criterion: 'Criterios de conservación',
};

/** null = el catalogo no tiene bandera propia (los 3 "simples": seguridad/transferencia/conservacion). */
const FLAG_LABEL: Record<CatalogName, string | null> = {
  legal_basis: 'Requiere base reforzada',
  data_category: 'Dato sensible',
  subject_category: 'Grupo protegido',
  recipient_type: 'Actúa como encargado',
  security_measure: null,
  transfer_guarantee_type: null,
  retention_criterion: null,
};

const CATALOG_ORDER: CatalogName[] = [
  'legal_basis',
  'data_category',
  'subject_category',
  'recipient_type',
  'security_measure',
  'transfer_guarantee_type',
  'retention_criterion',
];

export function CatalogsAdmin() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['admin', 'catalogs'], queryFn: fetchAdminCatalogs });
  const [openCatalog, setOpenCatalog] = useState<CatalogName | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['admin', 'catalogs'] });
    void qc.invalidateQueries({ queryKey: ['catalogs'] }); // el asistente usa esta misma cache key
  };

  const updateMut = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Parameters<typeof updateCatalogItem>[1] }) => updateCatalogItem(id, patch),
    onSuccess: invalidate,
    onError: (e) => setErr(e instanceof Error ? e.message : 'No se pudo guardar el cambio.'),
  });

  const createMut = useMutation({
    mutationFn: createCatalogItem,
    onSuccess: invalidate,
    onError: (e) => setErr(e instanceof Error ? e.message : 'No se pudo crear el item.'),
  });

  return (
    <>
      <PageHeader
        eyebrow="Configuración institucional"
        title="Catálogos"
        sub="Listas de referencia que usa el asistente de 9 pasos. Desactivar un item lo retira del asistente sin afectar actividades que ya lo usan."
      />

      <QueryState isLoading={q.isLoading} error={q.error}>
        {err && (
          <div className="callout callout--crit" style={{ marginBottom: 12 }}>
            {err}
          </div>
        )}

        <div style={{ display: 'grid', gap: 10 }}>
          {CATALOG_ORDER.map((name) => (
            <CatalogSection
              key={name}
              name={name}
              items={q.data?.[name] ?? []}
              open={openCatalog === name}
              onToggleOpen={() => setOpenCatalog(openCatalog === name ? null : name)}
              onToggleActive={(item) => {
                setErr(null);
                updateMut.mutate({ id: item.id, patch: { is_active: !item.is_active } });
              }}
              onSaveLabel={(item, label) => {
                setErr(null);
                updateMut.mutate({ id: item.id, patch: { label } });
              }}
              onCreate={(input) => {
                setErr(null);
                createMut.mutate({ catalog: name, ...input });
              }}
              busy={updateMut.isPending || createMut.isPending}
            />
          ))}
        </div>
      </QueryState>
    </>
  );
}

function CatalogSection({
  name,
  items,
  open,
  onToggleOpen,
  onToggleActive,
  onSaveLabel,
  onCreate,
  busy,
}: {
  name: CatalogName;
  items: AdminCatalogItem[];
  open: boolean;
  onToggleOpen: () => void;
  onToggleActive: (item: AdminCatalogItem) => void;
  onSaveLabel: (item: AdminCatalogItem, label: string) => void;
  onCreate: (input: { code: string; label: string; flag?: boolean }) => void;
  busy: boolean;
}) {
  const activeCount = items.filter((i) => i.is_active).length;
  return (
    <div className="card">
      <button
        className="card__h"
        style={{ width: '100%', textAlign: 'left', border: 0, background: 'transparent', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 10 }}
        onClick={onToggleOpen}
      >
        <h3 style={{ fontSize: 15, flex: 1 }}>{CATALOG_LABEL[name]}</h3>
        <span className="muted" style={{ fontSize: 12 }}>
          {activeCount} activo{activeCount === 1 ? '' : 's'} · {items.length} total
        </span>
        <span className="muted" style={{ fontSize: 12 }}>{open ? '▾' : '▸'}</span>
      </button>
      {open && (
        <div className="card__b" style={{ display: 'grid', gap: 8 }}>
          {items.map((item) => (
            <CatalogItemRow key={item.id} catalog={name} item={item} onToggleActive={onToggleActive} onSaveLabel={onSaveLabel} busy={busy} />
          ))}
          {items.length === 0 && <p className="muted" style={{ fontSize: 12 }}>Sin items todavía.</p>}
          <NewItemForm catalog={name} onCreate={onCreate} busy={busy} />
        </div>
      )}
    </div>
  );
}

function CatalogItemRow({
  catalog,
  item,
  onToggleActive,
  onSaveLabel,
  busy,
}: {
  catalog: CatalogName;
  item: AdminCatalogItem;
  onToggleActive: (item: AdminCatalogItem) => void;
  onSaveLabel: (item: AdminCatalogItem, label: string) => void;
  busy: boolean;
}) {
  const [label, setLabel] = useState(item.label);
  const dirty = label.trim() !== item.label && label.trim().length > 0;
  const flagLabel = FLAG_LABEL[catalog];

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '6px 8px',
        border: '1px solid var(--border)',
        borderRadius: 6,
        opacity: item.is_active ? 1 : 0.55,
      }}
    >
      <code className="mono" style={{ fontSize: 11, color: 'var(--muted)', minWidth: 130 }}>
        {item.code}
      </code>
      <input value={label} onChange={(e) => setLabel(e.target.value)} style={{ flex: 1, minWidth: 120 }} />
      {flagLabel && (
        <span className="muted" style={{ fontSize: 11, whiteSpace: 'nowrap' }} title={flagLabel}>
          {item.flag ? `✓ ${flagLabel}` : '—'}
        </span>
      )}
      {dirty && (
        <button className="btn btn--primary" style={{ fontSize: 11, padding: '3px 8px' }} disabled={busy} onClick={() => onSaveLabel(item, label.trim())}>
          Guardar
        </button>
      )}
      <button className="btn btn--ghost" style={{ fontSize: 11, padding: '3px 8px' }} disabled={busy} onClick={() => onToggleActive(item)}>
        {item.is_active ? 'Desactivar' : 'Reactivar'}
      </button>
    </div>
  );
}

function NewItemForm({
  catalog,
  onCreate,
  busy,
}: {
  catalog: CatalogName;
  onCreate: (input: { code: string; label: string; flag?: boolean }) => void;
  busy: boolean;
}) {
  const [adding, setAdding] = useState(false);
  const [code, setCode] = useState('');
  const [label, setLabel] = useState('');
  const [flag, setFlag] = useState(false);
  const flagLabel = FLAG_LABEL[catalog];
  const valid = /^[a-z][a-z0-9_]*$/.test(code) && label.trim().length > 0;

  if (!adding) {
    return (
      <button className="btn" style={{ justifySelf: 'start' }} onClick={() => setAdding(true)}>
        + Agregar item
      </button>
    );
  }

  return (
    <div style={{ display: 'grid', gap: 6, borderTop: '1px solid var(--border)', paddingTop: 8, marginTop: 2 }}>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        <input
          placeholder="codigo_interno"
          value={code}
          onChange={(e) => setCode(e.target.value.trim().toLowerCase())}
          style={{ flex: 1, minWidth: 140 }}
        />
        <input placeholder="Etiqueta visible" value={label} onChange={(e) => setLabel(e.target.value)} style={{ flex: 2, minWidth: 180 }} />
      </div>
      <p className="muted" style={{ fontSize: 11, margin: 0 }}>
        El código no se puede cambiar después de creado (solo minúsculas, números y guion bajo).
      </p>
      {flagLabel && (
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
          <input type="checkbox" checked={flag} onChange={(e) => setFlag(e.target.checked)} />
          {flagLabel}
        </label>
      )}
      <div style={{ display: 'flex', gap: 6 }}>
        <button
          className="btn btn--primary"
          disabled={!valid || busy}
          onClick={() => {
            onCreate({ code, label: label.trim(), flag: flagLabel ? flag : undefined });
            setAdding(false);
            setCode('');
            setLabel('');
            setFlag(false);
          }}
        >
          Crear
        </button>
        <button className="btn btn--ghost" onClick={() => setAdding(false)}>
          Cancelar
        </button>
      </div>
    </div>
  );
}
